import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, EyeOff, Brain, Lock, Fingerprint, Globe, UserCheck, AlertTriangle } from "lucide-react";
import SEO from "../components/SEO";

const PILLAR_ICONS = [Brain, ShieldCheck, EyeOff, Fingerprint, UserCheck, Lock];
const COMP_ICONS = [Globe, Lock, UserCheck];

const SecurityPage = () => {
  const { t } = useTranslation();
  const pillars = t("security.pillars.items", { returnObjects: true });
  const rules = t("security.rules.items", { returnObjects: true });
  const compliance = t("security.compliance", { returnObjects: true });

  return (
    <div data-testid="security-page">
      <SEO
        path="/seguridad"
        title={t("seo.security.title")}
        description={t("seo.security.description")}
        keywords={t("seo.security.keywords")}
      />
      {/* Hero */}
      <section className="hero" style={styles.hero}>
        <div className="container">
          <div className="heroInner" style={styles.heroInner}>
            <div className="eyebrow" style={styles.eyebrow}>{t("security.hero.eyebrow")}</div>
            <h1 className="heroTitle" style={styles.heroTitle}>
              {t("security.hero.titleLine1")}<br />{t("security.hero.titleLine2")}
            </h1>
            <p className="heroSubtitle" style={styles.heroSubtitle}>{t("security.hero.subtitle")}</p>
            <div className="ctaRow" style={styles.ctaRow}>
              <Link to="/contacto">
                <button className="btn-primary" data-testid="security-hero-cta">
                  {t("common.talkToExpert")}
                  <ArrowRight size={18} style={{ marginLeft: 8 }} />
                </button>
              </Link>
              <Link to="/producto" className="secondaryLink" style={styles.secondaryLink} data-testid="security-hero-link-product">
                {t("common.viewProduct")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="pillarsSection" style={styles.pillarsSection}>
        <div className="container">
          <div className="sectionHeader" style={styles.sectionHeader}>
            <h2 className="sectionTitle" style={styles.sectionTitle}>{t("security.pillars.title")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("security.pillars.subtitle")}</p>
          </div>
          <div className="pillarsGrid" style={styles.pillarsGrid}>
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || ShieldCheck;
              return (
                <div
                  key={i}
                  className="stack-card pillarCard"
                  style={styles.pillarCard}
                  data-testid={`security-pillar-${i}`}
                >
                  <div className="pillarIcon" style={styles.pillarIcon}>
                    <Icon size={22} strokeWidth={1.75} />
                  </div>
                  <h3 className="stack-title pillarTitle" style={styles.pillarTitle}>{p.title}</h3>
                  <p className="stack-description pillarDesc" style={styles.pillarDesc}>{p.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rules split */}
      <section className="rulesSection" style={styles.rulesSection}>
        <div className="container">
          <div className="rulesGrid" style={styles.rulesGrid}>
            <div className="rulesLeft" style={styles.rulesLeft}>
              <div className="eyebrowAlt" style={styles.eyebrowAlt}>{t("security.rules.eyebrow")}</div>
              <h2 className="rulesTitle" style={styles.rulesTitle}>{t("security.rules.titleLine1")}<br />{t("security.rules.titleLine2")}</h2>
              <p className="rulesDesc" style={styles.rulesDesc}>{t("security.rules.description")}</p>
              <Link to="/contacto" className="rulesLink" style={styles.rulesLink} data-testid="security-rules-link">
                {t("security.rules.link")}
              </Link>
            </div>
            <div className="rulesRight" style={styles.rulesRight}>
              <ul className="rulesList" style={styles.rulesList}>
                {rules.map((r, i) => (
                  <li key={i} className="rulesItem" style={styles.rulesItem} data-testid={`security-rule-${i}`}>
                    <AlertTriangle size={18} color="#f5576c" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance strip */}
      <section className="complianceSection" style={styles.complianceSection}>
        <div className="container">
          <div className="compGrid" style={styles.compGrid}>
            {compliance.map((c, i) => {
              const Icon = COMP_ICONS[i] || Globe;
              return (
                <div key={i} className="compItem" style={styles.compItem}>
                  <div className="compIcon" style={styles.compIcon}><Icon size={22} strokeWidth={1.75} /></div>
                  <div className="compTitle" style={styles.compTitle}>{c.title}</div>
                  <div className="compDesc" style={styles.compDesc}>{c.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaSection" style={styles.ctaSection}>
        <div className="container">
          <div className="ctaInner" style={styles.ctaInner}>
            <h2 className="ctaTitle" style={styles.ctaTitle}>{t("security.cta.title")}</h2>
            <p className="ctaSubtitle" style={styles.ctaSubtitle}>{t("security.cta.subtitle")}</p>
            <Link to="/contacto">
              <button className="btn-primary ctaButton" style={styles.ctaButton} data-testid="security-final-cta">
                {t("security.cta.button")}
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: {
    padding: "160px 20px 80px",
    background: "#ffffff"
  },
  heroInner: { maxWidth: "900px", margin: "0 auto" },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "20px"
  },
  eyebrowAlt: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#667eea",
    marginBottom: "16px"
  },
  heroTitle: {
    fontSize: "64px",
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px"
  },
  heroSubtitle: {
    fontSize: "20px",
    lineHeight: 1.6,
    color: "#4a5568",
    maxWidth: "720px",
    marginBottom: "36px"
  },
  ctaRow: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap"
  },
  secondaryLink: {
    color: "#667eea",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "16px"
  },
  pillarsSection: {
    padding: "100px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)"
  },
  sectionHeader: {
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto 60px"
  },
  sectionTitle: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "14px",
    letterSpacing: "-0.02em"
  },
  sectionSubtitle: {
    fontSize: "18px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  pillarsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  pillarCard: {
    padding: "32px",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "16px"
  },
  pillarIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "#f1f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea",
    marginBottom: "20px"
  },
  pillarTitle: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "10px",
    letterSpacing: "-0.01em"
  },
  pillarDesc: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: 1.65
  },
  rulesSection: {
    padding: "100px 20px",
    background: "#ffffff"
  },
  rulesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "60px",
    alignItems: "center",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  rulesLeft: { textAlign: "left" },
  rulesTitle: {
    fontSize: "44px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px",
    letterSpacing: "-0.02em"
  },
  rulesDesc: {
    fontSize: "17px",
    color: "#4a5568",
    lineHeight: 1.7,
    marginBottom: "24px"
  },
  rulesLink: {
    color: "#f5576c",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "16px"
  },
  rulesRight: {
    padding: "36px",
    background: "#f8f9fc",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "16px"
  },
  rulesList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "14px"
  },
  rulesItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    fontSize: "15px",
    color: "#2d3748",
    lineHeight: 1.5
  },
  complianceSection: {
    padding: "80px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)"
  },
  compGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
    maxWidth: "1100px",
    margin: "0 auto"
  },
  compItem: {
    padding: "28px",
    background: "#ffffff",
    borderRadius: "14px",
    border: "1px solid rgba(0,0,0,0.08)"
  },
  compIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "#f1f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea",
    marginBottom: "16px"
  },
  compTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "6px",
    letterSpacing: "-0.01em"
  },
  compDesc: {
    fontSize: "14px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  ctaSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0,0,0,0.08)"
  },
  ctaInner: {
    maxWidth: "700px",
    margin: "0 auto",
    textAlign: "center"
  },
  ctaTitle: {
    fontSize: "52px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px",
    letterSpacing: "-0.02em"
  },
  ctaSubtitle: {
    fontSize: "19px",
    color: "#4a5568",
    lineHeight: 1.6,
    marginBottom: "36px"
  },
  ctaButton: {
    fontSize: "17px",
    padding: "16px 36px"
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    [data-testid="security-page"] div[style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
    [data-testid="security-page"] div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
    [data-testid="security-page"] h1 { font-size: 44px !important; }
    [data-testid="security-page"] h2 { font-size: 38px !important; }
  }
  @media (max-width: 640px) {
    [data-testid="security-page"] section:first-child { padding: 120px 16px 60px !important; }
    [data-testid="security-page"] h1 { font-size: 34px !important; }
    [data-testid="security-page"] h2 { font-size: 28px !important; }
    [data-testid^="security-pillar-"] { padding: 24px !important; }
  }
`;
document.head.appendChild(styleSheet);

export default SecurityPage;
